import { type OperationId, operationIds } from "@gen/app-access-config";
import {
  ConfirmF2fHandoverBody,
  type ConfirmF2fHandoverBodyOn_behalf_of,
  CoreQuery,
} from "@gen/app-query";
import Button from "@mui/material/Button";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { ActionDialog } from "~/components/action_dialog";

interface ConfirmF2fHandoverActionProps {
  canExecuteForStatus: (operationId: OperationId) => boolean;
  userTransactionRole: "buyer" | "seller";
  transactionId: number;
}

export function ConfirmF2fHandoverAction({
  canExecuteForStatus,
  userTransactionRole,
  transactionId,
}: ConfirmF2fHandoverActionProps) {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { handleSubmit } = useForm({ defaultValues: {} });

  const { mutate: confirmF2fHandover, isPending: isConfirmingF2fHandover } =
    CoreQuery.useAdmin_confirmF2fHandoverMutation(transactionId, {
      meta: {
        success: `F2F handover confirmed for ${userTransactionRole}`,
      },
      onSuccess: () => {
        if (transactionId) {
          queryClient.invalidateQueries({
            queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
          });
        }
        setDialogOpen(false);
      },
    });

  if (!canExecuteForStatus(operationIds["core_admin.confirmF2fHandover"])) {
    return null;
  }

  const onSubmit = () => {
    const body = ConfirmF2fHandoverBody.fromJS({
      on_behalf_of: userTransactionRole as ConfirmF2fHandoverBodyOn_behalf_of,
    });

    confirmF2fHandover(body);
  };

  return (
    <>
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={() => setDialogOpen(true)}
        data-testid={`confirm-handover-${userTransactionRole}-button`}
      >
        Confirm handover for{" "}
        {userTransactionRole[0].toUpperCase() + userTransactionRole.slice(1)}
      </Button>
      {isDialogOpen && (
        <ActionDialog
          heading="Confirm handover"
          message="Are you sure you want to confirm the handover?"
          confirmButtonMessage="Yes, confirm the handover"
          cancelButtonMessage="Cancel handover"
          isConfirming={isConfirmingF2fHandover}
          confirm={handleSubmit(onSubmit)}
          cancel={() => setDialogOpen(false)}
          dataTestId={`confirm-handover-${userTransactionRole}`}
        />
      )}
    </>
  );
}
